import React, { ChangeEvent, useEffect, useState } from "react";
import {
  Backdrop,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Divider,
  FormControlLabel,
  Grid,
  IconButton,
  Modal,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import PhotoCamera from "@mui/icons-material/PhotoCamera";
import AddPhotoAlternateIcon from "@mui/icons-material/AddPhotoAlternate";
import { useNavigate, useParams } from "react-router-dom";
import { FetchResult, useMutation } from "@apollo/client";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as Yup from "yup";
import FormTextField from "../../libs/ui/FormTextField";
import FormNumberField from "../../libs/ui/FormNumberField";
import FormEditorField from "../../libs/ui/FormEditorField";
import { CustomTableToolbar } from "../../libs/ui/TableToolbar";
import {
  UpdateTestimonialPageDocument,
  AddTestimonialSectionDocument,
  UpdateTestimonialSectionDocument,
  DeleteTestimonialSectionDocument,
  AddTestimonialSectionGalleryDocument,
  UpdateTestimonialSectionGalleryDocument,
  DeleteTestimonialSectionGalleryDocument,
  GetPreSignedUrlDocument,
  GetPreSignedUrlMutation,
  useMinistriesQuery,
} from "../../graphql/generated/graphql";
import { uploadFile } from "../../libs/utils/fileupload";
import { showSuccess, showError } from "../../libs/utils/toasts";
import FormDropdownField from "../../libs/ui/FormDropdownField";

const modalBoxStyle = {
  width: "90%",
  maxHeight: "90vh",
  backgroundColor: "#fff",
  padding: 3,
  overflow: "auto",
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
};

interface GalleryType {
  id?: number;
  footer?: string;
  imageUrl: string;
  order: number;
  previewImage?: string;
  file?: File;
  parentId?: number;
}

interface SectionType {
  id?: number;
  title: string;
  content: string;
  addButton?: boolean;
  button?: string;
  buttonUrl?: string;
  enabledButton?: boolean;
  order: number;
  parentId?: number;
  galleries: GalleryType[];
}

interface TestimonialPageEditContainerProps {
  testimonialPage: any;
}

const TestimonialPageEditContainer = ({
  testimonialPage,
}: TestimonialPageEditContainerProps) => {
  const navigate = useNavigate();
  const { testimonialId } = useParams();

  const [loading, setLoading] = useState(false);
  const [mediaType, setMediaType] = useState("image");
  const [ministries, setMinistries] = useState<any[]>([]);
  const [sections, setSections] = useState<SectionType[]>([]);
  const [sectionModalVisible, setSectionModalVisible] = useState(false);
  const [galleryModalVisible, setGalleryModalVisible] = useState(false);
  const [selectSectionIndex, setSelectSectionIndex] = useState<
    number | undefined
  >(undefined);
  const [currentGalleries, setCurrentGalleries] = useState<GalleryType[]>([]);
  const [selectGalleryIndex, setSelectGalleryIndex] = useState<
    number | undefined
  >(undefined);
  const [galleryPreview, setGalleryPreview] = useState<string | null>(null);
  const [galleryFile, setGalleryFile] = useState<File | null>(null);
  const [addButton, setAddButton] = useState(false);
  const [deletedGalleryIds, setDeletedGalleryIds] = useState<number[]>([]);
  const [logoPreview, setLogoPreview] = useState<string | null>(null);
  const [logoFile, setLogoFile] = useState<File | null>(null);

  const [getPreSignedUrlMutation] = useMutation(GetPreSignedUrlDocument);
  const [updateTestimonialPage] = useMutation(UpdateTestimonialPageDocument);
  const [addSection] = useMutation(AddTestimonialSectionDocument);
  const [updateSection] = useMutation(UpdateTestimonialSectionDocument);
  const [deleteSection] = useMutation(DeleteTestimonialSectionDocument);
  const [addGallery] = useMutation(AddTestimonialSectionGalleryDocument);
  const [updateGallery] = useMutation(UpdateTestimonialSectionGalleryDocument);
  const [deleteGallery] = useMutation(DeleteTestimonialSectionGalleryDocument);

  const { data: ministriesData } = useMinistriesQuery({
    variables: { page: 1, pageSize: 100 },
    nextFetchPolicy: "network-only",
  });

  useEffect(() => {
    if (ministriesData?.Ministries.items) {
      const array = ministriesData?.Ministries.items.map((item) => ({
        label: item.name,
        value: item.id,
      }));
      setMinistries(array);
    }
  }, [ministriesData]);

  useEffect(() => {
    if (testimonialPage?.logoUrl) {
      setLogoPreview(testimonialPage.logoUrl);
    }
  }, [testimonialPage?.logoUrl]);

  useEffect(() => {
    if (testimonialPage?.sections) {
      const mappedSections = testimonialPage.sections.map((section: any) => ({
        id: section.id,
        title: section.title,
        content: section.content,
        addButton: !!(section.button || section.buttonUrl),
        button: section.button,
        buttonUrl: section.buttonUrl,
        enabledButton: section.enabledButton,
        order: section.order,
        parentId: section.parentId,
        galleries:
          section.galleries?.map((gallery: any) => ({
            id: gallery.id,
            footer: gallery.footer,
            imageUrl: gallery.imageUrl,
            order: gallery.order,
            parentId: gallery.parentId,
          })) || [],
      }));
      setSections(mappedSections);
    }
  }, [testimonialPage]);

  const validationSchema = Yup.object().shape({
    name: Yup.string().required("Name is required"),
    pageTitle: Yup.string().required("Page Title is required"),
    order: Yup.number().required("Order is required"),
    url: Yup.string().required("Page Url is required"),
    ministryId: Yup.number(),
    enabledGallery: Yup.boolean(),
    galleryButtonName: Yup.string(),
    galleryButtonPathUrl: Yup.string(),
    headLine: Yup.string(),
    summary: Yup.string(),
  });

  const methods = useForm({
    defaultValues: {
      name: testimonialPage?.name || "",
      pageTitle: testimonialPage?.pageTitle || "",
      enabledGallery: testimonialPage?.enabledGallery || false,
      galleryButtonName: testimonialPage?.galleryButtonName || "",
      galleryButtonPathUrl: testimonialPage?.galleryButtonPathUrl || "",
      order: testimonialPage?.order || 0,
      url: testimonialPage?.url || "",
      ministryId: testimonialPage?.ministryId || undefined,
      headLine: testimonialPage?.headLine || "",
      summary: testimonialPage?.summary || "",
    },
    resolver: yupResolver(validationSchema),
  });

  const { handleSubmit, control, getValues } = methods;

  const sectionValidationSchema = Yup.object().shape({
    title: Yup.string().required("Title is required"),
    content: Yup.string().required("Content is required"),
    enabledButton: Yup.boolean(),
    button: Yup.string().when("$addButton", {
      is: true,
      then: (schema) =>
        schema.required("Button text is required when button is enabled"),
      otherwise: (schema) => schema,
    }),
    buttonUrl: Yup.string().when("$addButton", {
      is: true,
      then: (schema) =>
        schema.required("Button URL is required when button is enabled"),
      otherwise: (schema) => schema,
    }),
    order: Yup.number().required("Order is required"),
  });

  const sectionMethods = useForm({
    defaultValues: {
      title: "",
      content: "",
      button: "",
      buttonUrl: "",
      enabledButton: false,
      order: 0,
    },
    resolver: yupResolver(sectionValidationSchema),
    context: { addButton },
  });

  const {
    handleSubmit: sectionHandleSubmit,
    control: sectionControl,
    getValues: sectionGetValues,
    reset: sectionReset,
    watch: sectionWatch,
  } = sectionMethods;

  const galleryValidationSchema = Yup.object().shape({
    footer: Yup.string(),
    order: Yup.number().required("Order is required"),
    youtubeUrl: Yup.string(),
  });

  const galleryMethods = useForm({
    defaultValues: {
      footer: "",
      order: 0,
      youtubeUrl: "",
    },
    resolver: yupResolver(galleryValidationSchema),
  });

  const {
    handleSubmit: galleryHandleSubmit,
    control: galleryControl,
    getValues: galleryGetValues,
    reset: galleryReset,
    setValue: gallerySetValue,
  } = galleryMethods;

  const handleFileUpload = async (file: File): Promise<string> => {
    if (file) {
      const { name, type } = file;
      const presignedName = `${new Date().getTime()}-${name}`;
      const { data }: FetchResult<GetPreSignedUrlMutation> =
        await getPreSignedUrlMutation({
          variables: { key: { id: presignedName } },
        });
      if (data?.GetPreSignedUrl.status) {
        return await uploadFile({
          file: file,
          presignedUrl: `${data?.GetPreSignedUrl.data}`,
          type,
          presignedName,
        });
      }
    }
    return "";
  };

  const handleLogoFileSelected = (event: ChangeEvent<HTMLInputElement>) => {
    const target = event.currentTarget as HTMLInputElement;
    const _file = target?.files && target?.files[0];
    setLogoFile(_file || null);

    if (_file) {
      let reader = new FileReader();
      reader.onload = (e) => {
        setLogoPreview(e.target?.result as string);
      };
      reader.readAsDataURL(_file);
    }
  };

  const handleGalleryFileSelected = (event: ChangeEvent<HTMLInputElement>) => {
    const target = event.currentTarget as HTMLInputElement;
    const _file = target?.files && target?.files[0];
    setGalleryFile(_file || null);

    if (_file) {
      let reader = new FileReader();
      reader.onload = (e) => {
        setGalleryPreview(e.target?.result as string);
      };
      reader.readAsDataURL(_file);
    }
  };

  const addOrUpdateGallery = async () => {
    const youtubeUrl = galleryGetValues("youtubeUrl");

    if (mediaType === "youtube") {
      if (!youtubeUrl) {
        showError("Please enter a YouTube URL");
        return;
      }
    } else {
      if (!galleryFile && !galleryPreview) {
        showError("Please select an image");
        return;
      }
    }

    let imageUrl = "";

    if (mediaType === "youtube") {
      imageUrl = youtubeUrl!;
    } else if (galleryFile) {
      setLoading(true);
      imageUrl = await handleFileUpload(galleryFile);
      setLoading(false);
      if (!imageUrl) {
        showError("Error uploading image");
        return;
      }
    } else if (typeof selectGalleryIndex === "number") {
      imageUrl = currentGalleries[selectGalleryIndex].imageUrl;
    }

    const gallery: GalleryType = {
      id:
        typeof selectGalleryIndex === "number"
          ? currentGalleries[selectGalleryIndex].id
          : undefined,
      footer: galleryGetValues("footer"),
      order: Number(galleryGetValues("order")),
      imageUrl: imageUrl,
      previewImage: mediaType === "image" ? galleryPreview || "" : "",
      file: undefined,
    };

    if (typeof selectGalleryIndex === "number") {
      const updated = [...currentGalleries];
      updated[selectGalleryIndex] = gallery;
      setCurrentGalleries(updated);
    } else {
      setCurrentGalleries([...currentGalleries, gallery]);
    }

    setGalleryModalVisible(false);
    setSelectGalleryIndex(undefined);
    setGalleryPreview(null);
    setGalleryFile(null);
    setMediaType("image");
    galleryReset({ footer: "", order: 0, youtubeUrl: "" });
  };

  const deleteGalleryFromSection = (index: number) => {
    const gallery = currentGalleries[index];

    if (gallery.id) {
      setDeletedGalleryIds([...deletedGalleryIds, gallery.id]);
    }

    const filtered = currentGalleries.filter((_, i) => i !== index);
    setCurrentGalleries(filtered);
  };

  const addOrUpdateSection = async () => {
    setLoading(true);
    try {
      const sectionData = {
        id:
          typeof selectSectionIndex === "number"
            ? sections[selectSectionIndex].id
            : undefined,
        title: sectionGetValues("title"),
        content: sectionGetValues("content"),
        button: addButton ? sectionGetValues("button") : undefined,
        buttonUrl: addButton ? sectionGetValues("buttonUrl") : undefined,
        order: Number(sectionGetValues("order")),
        enabledButton: sectionGetValues("enabledButton") || false,
        parentId: Number(testimonialId),
      };

      let sectionId = sectionData.id;

      if (sectionId) {
        for (const galleryId of deletedGalleryIds) {
          await deleteGallery({ variables: { id: galleryId } });
        }

        for (const gallery of currentGalleries) {
          let imageUrl = gallery.imageUrl;

          if (gallery.file) {
            imageUrl = await handleFileUpload(gallery.file);
          }

          const galleryData = {
            id: gallery.id,
            footer: gallery.footer,
            imageUrl: imageUrl,
            order: gallery.order,
            parentId: sectionId,
          };

          if (gallery.id) {
            await updateGallery({ variables: { data: galleryData } });
          } else {
            await addGallery({ variables: { data: galleryData } });
          }
        }

        const { data } = await updateSection({
          variables: {
            data: sectionData,
            galleries: currentGalleries.map((g) => ({
              id: g.id,
              footer: g.footer,
              imageUrl: g.imageUrl,
              order: g.order,
              parentId: sectionId,
            })),
          },
        });

        if (data?.UpdateTestimonialSection.status) {
          const updatedSection: SectionType = {
            id: data.UpdateTestimonialSection.data.id,
            title: data.UpdateTestimonialSection.data.title,
            content: data.UpdateTestimonialSection.data.content,
            addButton: addButton,
            button: data.UpdateTestimonialSection.data.button,
            buttonUrl: data.UpdateTestimonialSection.data.buttonUrl,
            order: data.UpdateTestimonialSection.data.order,
            parentId: data.UpdateTestimonialSection.data.parentId,
            galleries: data.UpdateTestimonialSection.data.galleries || [],
          };

          const updated = [...sections];
          updated[selectSectionIndex!] = updatedSection;
          setSections(updated);
          showSuccess("Section updated successfully");
          setLoading(false);
        }
      } else {
        const galleriesWithUrls = await Promise.all(
          currentGalleries.map(async (gallery) => {
            let imageUrl = gallery.imageUrl;
            if (gallery.file) {
              imageUrl = await handleFileUpload(gallery.file);
            }
            return {
              footer: gallery.footer,
              imageUrl: imageUrl,
              order: gallery.order,
            };
          })
        );

        const { data } = await addSection({
          variables: {
            data: {
              ...sectionData,
              galleries: galleriesWithUrls,
            },
          },
        });

        if (data?.AddTestimonialSection.status) {
          const newSection: SectionType = {
            id: data.AddTestimonialSection.data.id,
            title: data.AddTestimonialSection.data.title,
            content: data.AddTestimonialSection.data.content,
            addButton: addButton,
            button: data.AddTestimonialSection.data.button,
            buttonUrl: data.AddTestimonialSection.data.buttonUrl,
            order: data.AddTestimonialSection.data.order,
            parentId: data.AddTestimonialSection.data.parentId,
            galleries: data.AddTestimonialSection.data.galleries || [],
          };

          setSections([...sections, newSection]);
          showSuccess("Section created successfully");
          setLoading(false);
        }
      }

      setSectionModalVisible(false);
      setSelectSectionIndex(undefined);
      setCurrentGalleries([]);
      setAddButton(false);
      setDeletedGalleryIds([]);
      sectionReset({
        title: "",
        content: "",
        button: "",
        buttonUrl: "",
        order: 0,
      });
    } catch (error) {
      console.error("Error saving section:", error);
      showError("Error saving section");
    }
  };

  const deleteSectionFromPage = async (index: number) => {
    const section = sections[index];

    if (section.id) {
      try {
        const { data } = await deleteSection({
          variables: { id: section.id },
        });

        if (data?.DeleteTestimonialSection.status) {
          const filtered = sections.filter((_, i) => i !== index);
          setSections(filtered);
          showSuccess("Section deleted successfully");
        }
      } catch (error) {
        console.error("Error deleting section:", error);
        showError("Error deleting section");
      }
    } else {
      const filtered = sections.filter((_, i) => i !== index);
      setSections(filtered);
    }
  };

  const openSectionModal = (section?: SectionType, index?: number) => {
    if (section && typeof index === "number") {
      setSelectSectionIndex(index);
      // setSelectSection(section);
      setCurrentGalleries(section.galleries || []);
      setAddButton(section.enabledButton || false);
      sectionReset({
        title: section.title,
        content: section.content,
        button: section.button || "",
        buttonUrl: section.buttonUrl || "",
        enabledButton: section.enabledButton || false,
        order: section.order,
      });
    } else {
      setSelectSectionIndex(undefined);
      setCurrentGalleries([]);
      setAddButton(false);
      sectionReset({
        title: "",
        content: "",
        button: "",
        buttonUrl: "",
        enabledButton: false,
        order: 0,
      });
    }
    setDeletedGalleryIds([]);
    setSectionModalVisible(true);
  };

  const openGalleryModal = ({
    gallery,
    index,
    type,
  }: {
    gallery?: GalleryType;
    index?: number;
    type?: "image" | "youtube";
  }) => {
    if (type) {
      setMediaType(type);
    }

    if (gallery && typeof index === "number") {
      setSelectGalleryIndex(index);
      gallerySetValue("footer", gallery.footer || "");
      gallerySetValue("order", gallery.order);

      if (gallery.imageUrl && gallery.imageUrl.includes("youtube.com/embed")) {
        setMediaType("youtube");
        gallerySetValue("youtubeUrl", gallery.imageUrl);
        setGalleryPreview(null);
        setGalleryFile(null);
      } else {
        setMediaType("image");
        setGalleryPreview(gallery.previewImage || gallery.imageUrl);
        gallerySetValue("youtubeUrl", "");
      }
    } else {
      setSelectGalleryIndex(undefined);
      galleryReset();
      setGalleryPreview(null);
      setGalleryFile(null);
      if (type) {
        setMediaType(type);
      } else {
        setMediaType("image");
      }
    }
    setGalleryModalVisible(true);
  };

  const onUpdateTestimonialPage = async () => {
    setLoading(true);
    try {
      let logoUrl = testimonialPage?.logoUrl || "";

      if (logoFile) {
        logoUrl = await handleFileUpload(logoFile);
      }

      const testimonialPageData = {
        id: Number(testimonialId),
        name: getValues("name"),
        pageTitle: getValues("pageTitle"),
        order: Number(getValues("order")),
        url: getValues("url"),
        ministryId: getValues("ministryId")
          ? Number(getValues("ministryId"))
          : null,
        headLine: getValues("headLine"),
        summary: getValues("summary"),
        logoUrl: logoUrl,
        enabledGallery: getValues("enabledGallery") || false,
        galleryButtonName: getValues("enabledGallery")
          ? getValues("galleryButtonName")
          : null,
        galleryButtonPathUrl: getValues("enabledGallery")
          ? getValues("galleryButtonPathUrl")
          : null,
      };

      const { data } = await updateTestimonialPage({
        variables: {
          data: testimonialPageData,
        },
      });

      if (data?.UpdateTestimonialPage.status) {
        showSuccess("Testimonial Page updated successfully");
        navigate(-1);
      }
      setLoading(false);
    } catch (error) {
      console.error("Error updating testimonial page:", error);
      showError("Error updating testimonial page");
      setLoading(false);
    }
  };

  return (
    <Box p={3}>
      <Backdrop sx={() => ({ color: "#fff", zIndex: 1000000 })} open={loading}>
        <CircularProgress color="inherit" />
      </Backdrop>
      <Grid container>
        <Grid item xs={6} p={1}>
          <FormTextField
            name="name"
            label="Name"
            size="small"
            labelComponent={
              <Typography fontSize={16} fontWeight={500} lineHeight={2}>
                Name For Ministry Page
              </Typography>
            }
            control={control}
            inputProps={{ sx: { backgroundColor: "white" } }}
          />
        </Grid>

        <Grid item xs={6} p={1}>
          <FormTextField
            name="pageTitle"
            label="Page Title"
            size="small"
            labelComponent={
              <Typography fontSize={16} fontWeight={500} lineHeight={2}>
                Page Title For Testimonial Page
              </Typography>
            }
            control={control}
            inputProps={{ sx: { backgroundColor: "white" } }}
          />
        </Grid>

        <Grid item xs={4} p={1}>
          <FormNumberField
            name="order"
            label="Order"
            size="small"
            labelComponent={
              <Typography fontSize={16} fontWeight={500} lineHeight={2}>
                Order
              </Typography>
            }
            control={control}
            type={"number"}
            inputProps={{ sx: { backgroundColor: "white" } }}
          />
        </Grid>

        <Grid item xs={4} p={1}>
          <FormTextField
            name="url"
            label="Page Url"
            size="small"
            labelComponent={
              <Typography fontSize={16} fontWeight={500} lineHeight={2}>
                Page Url
              </Typography>
            }
            control={control}
            inputProps={{ sx: { backgroundColor: "white" } }}
          />
        </Grid>
        <Grid item xs={4} p={1}>
          <FormDropdownField
            name="ministryId"
            label="Show summary on Page / Ministry"
            size="small"
            disabled={ministries.length === 0}
            options={ministries}
            labelComponent={
              <Typography fontSize={16} fontWeight={500} lineHeight={2}>
                Show summary on Page / Ministry
              </Typography>
            }
            control={control}
            sx={{ background: "white" }}
          />
        </Grid>

        <Grid item xs={12} p={1}>
          <Typography fontSize={16} fontWeight={500} lineHeight={2} mb={1}>
            Logo
          </Typography>
          <Box display="flex" alignItems="center" gap={2}>
            <Box width={202} position="relative" lineHeight={1}>
              {!logoPreview && (
                <Box width={200} height={150}>
                  <IconButton
                    color="primary"
                    aria-label="upload logo"
                    component="label"
                    sx={{
                      width: "100%",
                      height: "100%",
                      border: "solid 1px gray",
                      borderRadius: "8px",
                      position: "absolute",
                      backgroundColor: "white",
                    }}
                  >
                    <input
                      hidden
                      accept="image/*"
                      type="file"
                      onChange={handleLogoFileSelected}
                    />
                    <PhotoCamera fontSize="large" />
                  </IconButton>
                </Box>
              )}
              {logoPreview && (
                <>
                  <img
                    src={logoPreview}
                    alt="logo-preview"
                    loading="lazy"
                    style={{
                      width: "200px",
                      height: "150px",
                      objectFit: "contain",
                      border: "solid 1px gray",
                      borderRadius: "8px",
                      backgroundColor: "#f5f5f5",
                    }}
                  />
                  <IconButton
                    color="primary"
                    aria-label="change logo"
                    component="label"
                    sx={{
                      width: "100%",
                      height: "100%",
                      position: "absolute",
                      left: 0,
                      top: 0,
                    }}
                  >
                    <input
                      hidden
                      accept="image/*"
                      type="file"
                      onChange={handleLogoFileSelected}
                    />
                    <PhotoCamera fontSize="large" sx={{ stroke: "white" }} />
                  </IconButton>
                </>
              )}
            </Box>
            {logoPreview && (
              <Button
                variant="outlined"
                color="error"
                size="small"
                onClick={() => {
                  setLogoPreview(null);
                  setLogoFile(null);
                }}
              >
                Remove Logo
              </Button>
            )}
          </Box>
        </Grid>

        <Grid item xs={12} p={1}>
          <Grid container alignItems="center">
            <FormControlLabel
              control={
                <Checkbox
                  checked={methods.watch("enabledGallery") || false}
                  onChange={(e) => {
                    methods.setValue("enabledGallery", e.target.checked);
                  }}
                />
              }
              label={
                <Typography fontSize={16} fontWeight={500} lineHeight={2}>
                  Add gallery URL
                </Typography>
              }
            />
          </Grid>

          {methods.watch("enabledGallery") && (
            <Grid container spacing={2} mt={1}>
              <Grid item xs={12} sm={6}>
                <FormTextField
                  name="galleryButtonName"
                  label="Button name"
                  size="small"
                  labelComponent={
                    <Typography fontSize={16} fontWeight={500} lineHeight={2}>
                      Button name
                    </Typography>
                  }
                  control={control}
                  inputProps={{ sx: { backgroundColor: "white" } }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormTextField
                  name="galleryButtonPathUrl"
                  label="Button path URL"
                  size="small"
                  labelComponent={
                    <Typography fontSize={16} fontWeight={500} lineHeight={2}>
                      Button path URL
                    </Typography>
                  }
                  control={control}
                  inputProps={{ sx: { backgroundColor: "white" } }}
                />
              </Grid>
            </Grid>
          )}
        </Grid>
        <Grid item xs={12} p={1}>
          <FormTextField
            name="headLine"
            label="Headline"
            size="small"
            labelComponent={
              <Typography fontSize={16} fontWeight={500} lineHeight={2}>
                Headline
              </Typography>
            }
            control={control}
            rows={2}
            inputProps={{ sx: { backgroundColor: "white" } }}
          />
        </Grid>
        <Grid item xs={12} p={1}>
          <FormEditorField
            name="summary"
            label="Summary For Ministry Page"
            defaultValue={testimonialPage?.summary || ""}
            labelComponent={
              <Typography fontSize={16} fontWeight={500} lineHeight={2}>
                Summary For Ministry Page
              </Typography>
            }
            control={control}
            rows={1}
            inputProps={{ sx: { backgroundColor: "white" } }}
            height={300}
          />
        </Grid>
      </Grid>

      <Divider sx={{ mt: 4 }} />

      <Box my={3}>
        <CustomTableToolbar
          title="Sections"
          numSelected={0}
          onDelete={() => {}}
          onAdd={() => openSectionModal()}
        />
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>No</TableCell>
                <TableCell>Title</TableCell>
                <TableCell>Order</TableCell>
                <TableCell>Galleries</TableCell>
                <TableCell width={80}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {sections.map((section, index) => (
                <TableRow key={index}>
                  <TableCell>{index + 1}</TableCell>
                  <TableCell>{section.title}</TableCell>
                  <TableCell>{section.order}</TableCell>
                  <TableCell>{section.galleries.length} images</TableCell>
                  <TableCell width={80} align="right">
                    <Box display="flex">
                      <IconButton
                        onClick={() => openSectionModal(section, index)}
                      >
                        <Tooltip title="Edit">
                          <EditIcon />
                        </Tooltip>
                      </IconButton>
                      <IconButton onClick={() => deleteSectionFromPage(index)}>
                        <Tooltip title="Delete">
                          <DeleteIcon />
                        </Tooltip>
                      </IconButton>
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>

      <Box my={4} display="flex" alignItems="center" justifyContent="flex-end">
        <Button
          variant="contained"
          color="primary"
          sx={{ width: "200px", height: "40px" }}
          onClick={handleSubmit(onUpdateTestimonialPage)}
        >
          Update
        </Button>
      </Box>

      <Modal
        open={sectionModalVisible}
        onClose={() => setSectionModalVisible(false)}
      >
        <Box sx={modalBoxStyle}>
          <Box bgcolor="#e3e3e3" borderRadius={1} p={1} mb={2}>
            <Typography fontWeight={500} textAlign="center">
              {typeof selectSectionIndex === "number"
                ? "Update Section"
                : "Add Section"}
            </Typography>
          </Box>

          <Grid container>
            <Grid item xs={12} p={1}>
              <FormTextField
                name="title"
                label="Title"
                size="small"
                labelComponent={
                  <Typography fontSize={16} fontWeight={500} lineHeight={2}>
                    Title
                  </Typography>
                }
                control={sectionControl}
                inputProps={{ sx: { backgroundColor: "white" } }}
              />
            </Grid>
            <Grid item xs={12} p={1}>
              <FormTextField
                name="content"
                label="Section content"
                rows={6}
                labelComponent={
                  <Typography fontSize={16} fontWeight={500} lineHeight={2}>
                    Content
                  </Typography>
                }
                control={sectionControl}
                inputProps={{ sx: { backgroundColor: "white" } }}
              />
            </Grid>
            <Grid item xs={12} p={1}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={sectionWatch("enabledButton") || false}
                    onChange={(e) => {
                      sectionMethods.setValue(
                        "enabledButton",
                        e.target.checked
                      );
                      setAddButton(e.target.checked);
                    }}
                  />
                }
                label="Add Button"
              />
            </Grid>

            {sectionWatch("enabledButton") && (
              <>
                <Grid item xs={6} p={1}>
                  <FormTextField
                    name="button"
                    label="Button Text"
                    size="small"
                    labelComponent={
                      <Typography fontSize={16} fontWeight={500} lineHeight={2}>
                        Button Text
                      </Typography>
                    }
                    control={sectionControl}
                    inputProps={{ sx: { backgroundColor: "white" } }}
                  />
                </Grid>

                <Grid item xs={6} p={1}>
                  <FormTextField
                    name="buttonUrl"
                    label="Button URL"
                    size="small"
                    labelComponent={
                      <Typography fontSize={16} fontWeight={500} lineHeight={2}>
                        Button URL
                      </Typography>
                    }
                    control={sectionControl}
                    inputProps={{ sx: { backgroundColor: "white" } }}
                  />
                </Grid>
              </>
            )}

            <Grid item xs={12} p={1}>
              <FormNumberField
                name="order"
                label="Order"
                size="small"
                labelComponent={
                  <Typography fontSize={16} fontWeight={500} lineHeight={2}>
                    Order
                  </Typography>
                }
                control={sectionControl}
                type={"number"}
                inputProps={{ sx: { backgroundColor: "white" } }}
              />
            </Grid>
          </Grid>

          <Divider sx={{ my: 2 }} />

          <Box my={2}>
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              mb={2}
            >
              <Typography variant="h6">Gallery Images</Typography>
              <Grid container justifyContent="flex-end" spacing={2}>
                <Grid item>
                  <Button
                    variant="contained"
                    size="small"
                    startIcon={<AddPhotoAlternateIcon />}
                    onClick={() =>
                      openGalleryModal({
                        type: "image",
                      })
                    }
                  >
                    Add Image
                  </Button>
                </Grid>
                <Grid item>
                  <Button
                    variant="contained"
                    size="small"
                    startIcon={<AddPhotoAlternateIcon />}
                    onClick={() =>
                      openGalleryModal({
                        type: "youtube",
                      })
                    }
                  >
                    Add youtube video
                  </Button>
                </Grid>
              </Grid>
            </Box>

            <Grid container spacing={2}>
              {currentGalleries.map((gallery, index) => (
                <Grid item xs={3} key={index}>
                  <Paper elevation={2} sx={{ p: 1 }}>
                    <Box position="relative">
                      {gallery.imageUrl.includes("youtube.com/embed") ? (
                        <Box
                          sx={{
                            width: "100%",
                            height: "150px",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            bgcolor: "#000",
                            borderRadius: "4px",
                            color: "white",
                          }}
                        >
                          <Typography variant="body2">YouTube Video</Typography>
                        </Box>
                      ) : (
                        <img
                          src={gallery.previewImage || gallery.imageUrl}
                          alt={gallery.footer}
                          style={{
                            width: "100%",
                            height: "150px",
                            objectFit: "cover",
                            borderRadius: "4px",
                          }}
                        />
                      )}
                      <Box
                        position="absolute"
                        top={5}
                        right={5}
                        display="flex"
                        gap={0.5}
                      >
                        <IconButton
                          size="small"
                          sx={{ bgcolor: "white" }}
                          onClick={() => openGalleryModal({ gallery, index })}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          sx={{ bgcolor: "white" }}
                          onClick={() => deleteGalleryFromSection(index)}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    </Box>
                    <Typography variant="caption" display="block" mt={1}>
                      {gallery.footer}
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      Order: {gallery.order}
                    </Typography>
                  </Paper>
                </Grid>
              ))}
            </Grid>
          </Box>

          <Box
            mt={4}
            display="flex"
            alignItems="center"
            justifyContent="flex-end"
          >
            <Button
              variant="contained"
              color="primary"
              sx={{ width: "200px", height: "40px" }}
              onClick={sectionHandleSubmit(addOrUpdateSection)}
            >
              {typeof selectSectionIndex === "number" ? "Update" : "Add"}
            </Button>
          </Box>
        </Box>
      </Modal>

      <Modal
        open={galleryModalVisible}
        onClose={() => setGalleryModalVisible(false)}
      >
        <Box sx={{ ...modalBoxStyle, width: "50%" }}>
          <Box bgcolor="#e3e3e3" borderRadius={1} p={1} mb={2}>
            <Typography fontWeight={500} textAlign="center">
              {typeof selectGalleryIndex === "number"
                ? "Update Image"
                : "Add Image"}
            </Typography>
          </Box>

          <Grid container>
            {mediaType === "image" && (
              <Grid item xs={12} p={1} display="flex" justifyContent="center">
                <Box width={202} position="relative" lineHeight={1}>
                  {!galleryPreview && (
                    <Box width={200} height={150}>
                      <IconButton
                        color="primary"
                        aria-label="upload picture"
                        component="label"
                        sx={{
                          width: "100%",
                          height: "100%",
                          border: "solid 1px gray",
                          borderRadius: "8px",
                          position: "absolute",
                        }}
                      >
                        <input
                          hidden
                          accept="image/*"
                          type="file"
                          onChange={handleGalleryFileSelected}
                        />
                        <PhotoCamera fontSize="large" />
                      </IconButton>
                    </Box>
                  )}
                  {galleryPreview && (
                    <>
                      <img
                        src={galleryPreview}
                        alt="gallery-preview"
                        loading="lazy"
                        style={{
                          width: "200px",
                          height: "150px",
                          objectFit: "cover",
                          border: "solid 1px gray",
                          borderRadius: "8px",
                        }}
                      />
                      <IconButton
                        color="primary"
                        aria-label="upload picture"
                        component="label"
                        sx={{
                          width: "100%",
                          height: "100%",
                          position: "absolute",
                          left: 0,
                          top: 0,
                        }}
                      >
                        <input
                          hidden
                          accept="image/*"
                          type="file"
                          onChange={handleGalleryFileSelected}
                        />
                        <PhotoCamera
                          fontSize="large"
                          sx={{ stroke: "white" }}
                        />
                      </IconButton>
                    </>
                  )}
                </Box>
              </Grid>
            )}

            {mediaType === "youtube" && (
              <Grid item xs={12} p={1}>
                <FormTextField
                  name="youtubeUrl"
                  label="YouTube URL"
                  size="small"
                  placeholder="https://www.youtube.com/embed/..."
                  labelComponent={
                    <Typography fontSize={16} fontWeight={500} lineHeight={2}>
                      YouTube Video URL
                    </Typography>
                  }
                  control={galleryControl}
                  inputProps={{ sx: { backgroundColor: "white" } }}
                />
              </Grid>
            )}

            <Grid item xs={12} p={1}>
              <FormTextField
                name="footer"
                label="Image Caption/Footer"
                size="small"
                labelComponent={
                  <Typography fontSize={16} fontWeight={500} lineHeight={2}>
                    Image Caption/Footer
                  </Typography>
                }
                control={galleryControl}
                inputProps={{ sx: { backgroundColor: "white" } }}
              />
            </Grid>

            <Grid item xs={12} p={1}>
              <FormNumberField
                name="order"
                label="Order"
                size="small"
                labelComponent={
                  <Typography fontSize={16} fontWeight={500} lineHeight={2}>
                    Order
                  </Typography>
                }
                control={galleryControl}
                type={"number"}
                inputProps={{ sx: { backgroundColor: "white" } }}
              />
            </Grid>
          </Grid>

          <Box
            mt={4}
            display="flex"
            alignItems="center"
            justifyContent="flex-end"
          >
            <Button
              variant="contained"
              color="primary"
              sx={{ width: "200px", height: "40px" }}
              onClick={galleryHandleSubmit(addOrUpdateGallery)}
            >
              {typeof selectGalleryIndex === "number" ? "Update" : "Add"}
            </Button>
          </Box>
        </Box>
      </Modal>
    </Box>
  );
};

export default TestimonialPageEditContainer;
